import type { TreeNodeResponse } from './cors-provenance-api';
import {
  fileInfoFromProvenanceBody,
  getMediaType,
  isArchiveMemberSearchablePath,
  resolveFileInfoMediaDisplayUrl,
  toProvenanceLookupPath,
} from './functions';
import type { MediaSource } from './types';

/**
 * Build the absolute searchable path for a tree node. Subdocuments and virtual
 * directories inside a container carry ``sourceFile.searchablePath`` on search
 * results; browse-mode nodes rely on the same ``{source}/{relative}`` shape.
 */
export function treeNodeAbsolutePath(
  node: TreeNodeResponse,
  contextSourceFilePath?: string,
): string {
  const sourcePath = node.sourceFile?.searchablePath ?? contextSourceFilePath ?? '';
  if (!sourcePath) return node.path;
  return `${sourcePath}/${node.path}`;
}

/** Canonical provenance lookup path for picker / file-info resolution. */
export function treeNodePickPath(node: TreeNodeResponse): string {
  if (node.nodeType === 'subdocument') {
    return treeNodeAbsolutePath(node);
  }
  return node.path;
}

export function treeNodePickUrl(node: TreeNodeResponse): string {
  return toProvenanceLookupPath(treeNodePickPath(node));
}

export function treeNodeToFileInfo(node: TreeNodeResponse) {
  return fileInfoFromProvenanceBody(node.fileInfo);
}

function nodeFileStatusId(node: TreeNodeResponse): string | undefined {
  // Search hits for members inside a container attach the parent on
  // ``sourceFile.id``. Prefer that (or ``fileStatusId``) so we can re-list
  // container children via ``GET /tree?fileStatusId=…``. Fall back to the
  // subdocument ``rootId`` / node's own id for top-level files.
  const fromFileInfo =
    node.fileInfo?.kind === 'subdocument'
      ? (typeof node.fileInfo.subdocument?.rootId === 'string'
          ? node.fileInfo.subdocument.rootId.trim()
          : '')
      : node.fileInfo?.kind === 'file'
        ? (typeof node.fileInfo.file?.fileStatusId === 'string'
            ? node.fileInfo.file.fileStatusId.trim()
            : typeof node.fileInfo.file?.id === 'string'
              ? node.fileInfo.file.id.trim()
              : '')
        : '';
  const id =
    (typeof node.fileStatusId === 'string' && node.fileStatusId.trim()) ||
    (typeof node.sourceFile?.id === 'string' && node.sourceFile.id.trim()) ||
    fromFileInfo ||
    (node.nodeType === 'file' && typeof node.id === 'string' ? node.id.trim() : '') ||
    '';
  return id || undefined;
}

/**
 * Map a provenance tree file/subdocument node to a ``MediaSource`` row for
 * UnifiedSources and other flat pickers. Directory nodes return ``null``.
 *
 * ``sourceUrl`` stays the searchable pick path (for list identity / scope).
 * Playable bytes come from ``fileInfo.fileUrl`` / ``previewUrl`` when those are
 * real storage URLs — never from a virtual ``…/archive.zip/inner`` path.
 */
export function treeNodeToMediaSource(node: TreeNodeResponse): MediaSource | null {
  if (node.nodeType === 'directory') return null;

  const sourceUrl = treeNodePickUrl(node);
  const fileInfo = treeNodeToFileInfo(node);
  const sourceType = getMediaType({ sourceUrl } as MediaSource, fileInfo);
  const playableUrl = resolveFileInfoMediaDisplayUrl(fileInfo, '');
  const fileStatusId = nodeFileStatusId(node);

  return {
    sourceUrl,
    sourceFilePath: sourceUrl,
    sourceType,
    status: fileInfo?.status ?? 'unknown',
    originalUrl: sourceUrl,
    ...(fileStatusId ? { fileStatusId } : {}),
    ...(fileInfo ? { fileInfo } : {}),
    ...(playableUrl
      ? {
          s3Url: playableUrl,
          mediaUrl: playableUrl,
          mediaSrc: playableUrl,
          mediaType: sourceType,
        }
      : fileInfo?.fileUrl && !isArchiveMemberSearchablePath(fileInfo.fileUrl)
        ? { s3Url: fileInfo.fileUrl }
        : {}),
  };
}

export function treeNodesToMediaSources(nodes: TreeNodeResponse[]): MediaSource[] {
  return nodes
    .map(treeNodeToMediaSource)
    .filter((source): source is MediaSource => source !== null);
}

/**
 * Find the exact tree node represented by a provenance lookup URL.
 *
 * Archive extracts frequently share a filename (``image.png``, ``document.pdf``)
 * with members of other containers. Matching only the final path component can
 * therefore attach another extract's file-info and processed URL.
 */
export function findTreeNodeForSourceUrl(
  nodes: TreeNodeResponse[],
  sourceUrl: string,
): TreeNodeResponse | undefined {
  const normalize = (value: string): string => {
    const withoutQuery = value.trim().split(/[?#]/)[0];
    try {
      return decodeURIComponent(toProvenanceLookupPath(withoutQuery))
        .replace(/^\/+/, '')
        .toLowerCase();
    } catch {
      return toProvenanceLookupPath(withoutQuery).replace(/^\/+/, '').toLowerCase();
    }
  };
  const target = normalize(sourceUrl);
  if (!target) return undefined;
  return nodes.find((n) => {
    if (n.nodeType === 'directory') return false;
    return normalize(treeNodePickUrl(n)) === target;
  });
}
